import React, {
  useCallback,
  useMemo,
  memo,
  useState,
  useEffect,
  useRef,
} from "react";
import {
  PanelLeftClose,
  GripVertical,
  Settings2,
  MoreVertical,
  Sparkles,
  ImageIcon,
  Copy as CopyIcon,
  Trash2,
} from "lucide-react";
import toast from "react-hot-toast";
import { PresentationSettings } from "@/components/slides/presentation-settings";
import { SlideSettings } from "@/components/slides/slide-settings";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useSlideStore } from "@/stores/slide-store";
import type { SlideInfo } from "@/stores/slide-store";
import { previewMessage } from "@/lib/visual-editor/post-message";
import { Hint } from "@/components/ui/hint";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from "@/components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAgentInputStore } from "@/stores/agent-input-store";

import { claudeAgentApi } from "@/lib/api/claude-agent";
import { useSlideThumbnail } from "@/hooks/use-slide-thumbnail";

interface SlidesPanelProps {
  previewIframeRef: React.RefObject<HTMLIFrameElement | null>;
  previewUrl?: string;
  projectId?: string;
  devContainerId?: string;
  getIdToken: () => Promise<string | null>;
}

export function SlidesPanel({
  previewIframeRef,
  devContainerId,
  getIdToken,
  projectId,
}: SlidesPanelProps) {
  const {
    slides,
    currentSlideIndex,
    isSidebarCollapsed,
    setSidebarCollapsed,
    setSlides,
  } = useSlideStore();

  // Each memoized per-slide row subscribes directly to `thumbnailRevision`
  // via useSlideStore — keeps the bump from re-rendering the whole panel.
  // The row's useSlideThumbnail hook depends on the revision and re-fetches
  // when it changes.

  const handleSlideClick = useCallback(
    (index: number) => {
      previewMessage.navigateToSlide(previewIframeRef.current, index);
      useSlideStore.getState().setCurrentSlide(index);
    },
    [previewIframeRef],
  );

  // DnD sensors with 5px activation distance to prevent accidental drags
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = slides.findIndex((s) => s.id === active.id);
      const newIndex = slides.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      // Optimistic update
      const newSlides = arrayMove(slides, oldIndex, newIndex);
      setSlides(newSlides);

      // Persist to sidecar — queued through runMutation so any follow-up
      // duplicate/delete on the same deck waits for this PUT to land before
      // reading the on-disk manifest. Without this, the sidecar can read a
      // pre-reorder manifest and clobber the optimistic order.
      if (!devContainerId) return;
      try {
        const token = await getIdToken();
        if (!token) return;
        const result = await useSlideStore.getState().runMutation(() =>
          claudeAgentApi.reorderSlides(
            token,
            devContainerId,
            newSlides.map((s) => s.id),
          ),
        );
        // Reconcile with server truth. The backend's reorder response
        // returns the authoritative `slides` array including any stragglers
        // it had to append (e.g. if our optimistic newSlides was missing a
        // slide that exists on disk from another tab). Override the
        // optimistic state so frontend doesn't diverge from server state.
        // API client types `slides?: unknown[]`; cast since backend's
        // contract matches SlideInfo[].
        if (result?.slides && Array.isArray(result.slides)) {
          setSlides(result.slides as SlideInfo[]);
        }
        // Reorder doesn't change slide content (only `position` in
        // slides.json), but bump revision so the per-slide hooks re-fetch
        // existing by-slide-id blobs — covers any in-memory cache that
        // survived the reorder. The backend also broadcasts
        // `slides-thumbnails-updated` for cross-tab sync.
        useSlideStore.getState().bumpThumbnailRevision();
      } catch (err) {
        // Revert on failure
        console.error("Failed to reorder slides:", err);
        setSlides(slides);
      }
    },
    [slides, devContainerId, getIdToken, setSlides],
  );

  // Edit with Agent — push a `@file:src/slides/SlideN.tsx` chip into the
  // agent input and request that the agent panel open. The agent-input
  // store is the synchronous seam; IntegratedEditorWithAgent watches
  // `shouldOpenPanel`, AgentChat drains `pendingChips`.
  const handleEditWithAgent = useCallback((slide: SlideInfo) => {
    const filename = slide.file.replace(/^\.\//, "");
    const path = `src/slides/${filename}`;
    useAgentInputStore.getState().injectFileMention(path);
  }, []);

  // Duplicate — server-driven: the sidecar generates the new id, filename,
  // and thumbnail path. We reflect the response and switch the editor's
  // current slide to the freshly created one.
  const handleDuplicate = useCallback(
    async (slide: SlideInfo) => {
      if (!devContainerId) return;
      try {
        const token = await getIdToken();
        if (!token) {
          toast.error("Not authorized");
          return;
        }
        const res = await useSlideStore.getState().runMutation(() =>
          claudeAgentApi.duplicateSlide(token, devContainerId, slide.id),
        );
        if (res.slides) {
          setSlides(res.slides);
          // Bump immediately so the new slide's thumbnail hook fires fresh
          // and any pre-existing slides re-fetch their by-slide-id blobs.
          // Backend kicks off a Puppeteer recapture for the new slide in
          // the background — the `slides-thumbnails-updated` broadcast will
          // re-bump revision once that finishes, giving the duplicate its
          // final fresh thumbnail.
          useSlideStore.getState().bumpThumbnailRevision();
        }
        if (res.newSlideId && res.slides) {
          const newIdx = res.slides.findIndex(
            (s: SlideInfo) => s.id === res.newSlideId,
          );
          if (newIdx !== -1) {
            useSlideStore.getState().setCurrentSlide(newIdx);
            previewMessage.navigateToSlide(previewIframeRef.current, newIdx);
          }
        }
        toast.success("Slide duplicated");
      } catch (err) {
        console.error("Failed to duplicate slide:", err);
        toast.error("Failed to duplicate slide");
      }
    },
    [devContainerId, getIdToken, setSlides, previewIframeRef],
  );

  // Delete — optimistic store mutation + iframe nudge, rollback on failure.
  // Mirrors the reorder pattern.
  const handleDeleteSlide = useCallback(
    async (slide: SlideInfo) => {
      if (!devContainerId) return;
      if (slides.length <= 1) return;

      const snapshot = slides;
      useSlideStore.getState().deleteSlide(slide.id);
      const nextCurrent = useSlideStore.getState().currentSlideIndex;
      previewMessage.navigateToSlide(previewIframeRef.current, nextCurrent);

      try {
        const token = await getIdToken();
        if (!token) {
          setSlides(snapshot);
          toast.error("Not authorized");
          return;
        }
        const result = await useSlideStore.getState().runMutation(() =>
          claudeAgentApi.deleteSlide(token, devContainerId, slide.id),
        );
        // Reconcile with server truth. The backend's delete response
        // returns the authoritative `slides` array after the removal +
        // position renumber. Override the optimistic state so any
        // parallel-tab divergence (e.g. another tab also modified the
        // deck) is corrected on this client too.
        if (result?.slides && Array.isArray(result.slides)) {
          setSlides(result.slides as SlideInfo[]);
        }
        // Trigger thumbnail revision bump — any per-slide hook still
        // showing the deleted slide's blob revokes/refreshes.
        useSlideStore.getState().bumpThumbnailRevision();
      } catch (err) {
        console.error("Failed to delete slide:", err);
        setSlides(snapshot);
        toast.error("Failed to delete slide");
      }
    },
    [slides, devContainerId, getIdToken, setSlides, previewIframeRef],
  );

  const slideIds = useMemo(() => slides.map((s) => s.id), [slides]);
  const canDelete = slides.length > 1;

  return (
    <div
      className={cn(
        "border-r border-border bg-card flex flex-col transition-all duration-200",
        isSidebarCollapsed
          ? "w-0 min-w-0 overflow-hidden border-r-0"
          : "w-[160px] min-w-[160px]",
      )}
    >
      {/* Header — matches top bar height */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <span className="text-xs font-medium text-foreground">Slides</span>
        <div className="flex items-center gap-0.5">
          <PresentationSettings
            previewIframeRef={previewIframeRef}
            devContainerId={devContainerId}
            getIdToken={getIdToken}
            projectId={projectId}
          />
          <Hint
            delayDuration={0}
            text="Hide slides"
            side="right"
            align="center"
          >
            <button
              onClick={() => setSidebarCollapsed(true)}
              className="p-1 hover:bg-accent rounded transition-colors"
            >
              <PanelLeftClose className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          </Hint>
        </div>
      </div>

      {/* Slide List */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1.5">
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <SortableContext
              items={slideIds}
              strategy={verticalListSortingStrategy}
            >
              {slides.map((slide: SlideInfo, index: number) => (
                <SortableSlideThumbnail
                  key={slide.id}
                  slide={slide}
                  index={index}
                  isCurrent={index === currentSlideIndex}
                  onClick={() => handleSlideClick(index)}
                  previewIframeRef={previewIframeRef}
                  devContainerId={devContainerId}
                  getIdToken={getIdToken}
                  canDelete={canDelete}
                  onEditWithAgent={() => handleEditWithAgent(slide)}
                  onDuplicate={() => handleDuplicate(slide)}
                  onDelete={() => handleDeleteSlide(slide)}
                />
              ))}
            </SortableContext>
          </DndContext>
          {slides.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-4">
              No slides yet
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

interface SlideThumbnailProps {
  slide: SlideInfo;
  index: number;
  isCurrent: boolean;
  onClick: () => void;
  previewIframeRef: React.RefObject<HTMLIFrameElement | null>;
  devContainerId?: string;
  getIdToken: () => Promise<string | null>;
  canDelete: boolean;
  onEditWithAgent: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

// JPEG → PNG conversion via canvas. Used as the second branch of the
// copy-screenshot chain when the browser's clipboard rejects image/jpeg
// (Safari < 16.4 and older Firefox). Returns a Promise<Blob> so the caller
// can pass it straight into `new ClipboardItem({ 'image/png': promise })` —
// awaiting the conversion BEFORE clipboard.write() loses the user-gesture
// activation token on Safari and produces NotAllowedError.
async function jpegBlobToPngBlob(blob: Blob): Promise<Blob> {
  const img = new Image();
  const url = URL.createObjectURL(blob);
  try {
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Failed to decode image"));
      img.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not get 2d context");
    ctx.drawImage(img, 0, 0);
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob returned null"))),
        "image/png",
      ),
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

const SortableSlideThumbnail = memo(function SortableSlideThumbnail({
  slide,
  index,
  isCurrent,
  onClick,
  previewIframeRef,
  devContainerId,
  getIdToken,
  canDelete,
  onEditWithAgent,
  onDuplicate,
  onDelete,
}: SlideThumbnailProps) {
  // Subscribe directly to thumbnailRevision so a bump re-renders ONLY this
  // memoized row (not the whole panel). Each row's useSlideThumbnail hook
  // depends on the revision and re-fetches when it changes.
  const revision = useSlideStore((s) => s.thumbnailRevision);
  const { url, error } = useSlideThumbnail(
    slide.id,
    devContainerId,
    getIdToken,
    revision,
  );
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slide.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const hasThumbnail = Boolean(url && !error);

  // Copy the rendered thumbnail to the clipboard. Chain: image/jpeg →
  // canvas-converted image/png → download fallback. The hook already holds
  // a blob URL; we fetch it back to get the underlying Blob (no network —
  // this resolves locally from the cached blob).
  const handleCopyScreenshot = useCallback(async () => {
    if (!url) return;
    try {
      const res = await fetch(url);
      const jpegBlob = await res.blob();
      try {
        await navigator.clipboard.write([
          new ClipboardItem({ "image/jpeg": jpegBlob }),
        ]);
        toast.success("Screenshot copied");
      } catch {
        try {
          // Pass the conversion as a Promise to preserve the user-gesture
          // activation token on Safari — don't await before clipboard.write.
          await navigator.clipboard.write([
            new ClipboardItem({ "image/png": jpegBlobToPngBlob(jpegBlob) }),
          ]);
          toast.success("Screenshot copied");
        } catch {
          // Final fallback: download the JPEG so the user can still share it.
          const dl = URL.createObjectURL(jpegBlob);
          const a = document.createElement("a");
          a.href = dl;
          a.download = `slide-${index + 1}.jpg`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(dl);
          toast.error("Clipboard blocked — image downloaded instead");
        }
      }
    } catch (err) {
      console.error("Failed to copy screenshot:", err);
      toast.error("Failed to copy screenshot");
    }
  }, [url, index]);

  // The four menu items are rendered twice — once as ContextMenuItems
  // (right-click surface) and once as DropdownMenuItems (hover-kebab
  // surface). Keeping them as inline JSX inside each menu keeps the menu
  // primitives' compound-component contracts intact (Radix is picky about
  // children being its own item components, not generic wrappers).
  const renderMenuItems = (
    Item: typeof ContextMenuItem | typeof DropdownMenuItem,
    Separator: typeof ContextMenuSeparator | typeof DropdownMenuSeparator,
  ) => (
    <>
      <Item
        onSelect={onEditWithAgent}
        disabled={!devContainerId}
        className="text-xs"
      >
        <Sparkles className="w-3.5 h-3.5 mr-2 text-primary" />
        Edit with Agent
      </Item>
      <Item
        onSelect={handleCopyScreenshot}
        disabled={!hasThumbnail}
        className="text-xs"
      >
        <ImageIcon className="w-3.5 h-3.5 mr-2" />
        Copy screenshot
      </Item>
      <Item
        onSelect={onDuplicate}
        disabled={!devContainerId}
        className="text-xs"
      >
        <CopyIcon className="w-3.5 h-3.5 mr-2" />
        Duplicate
      </Item>
      <Separator />
      <Item
        // onSelect (not onClick) fires before the menu unmounts. Radix's
        // state-batching means the AlertDialog mounts cleanly after the
        // menu closes — see project plan §11 (AlertDialog hoisted).
        onSelect={() => setConfirmDeleteOpen(true)}
        disabled={!canDelete}
        className="text-xs text-destructive focus:text-destructive"
      >
        <Trash2 className="w-3.5 h-3.5 mr-2" />
        Delete
      </Item>
    </>
  );

  // Inner element: drag handle + numbered indicator + thumbnail + overlays.
  // Wrapped by ContextMenu below so right-click anywhere on the row triggers
  // the menu.
  const rowContent = (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "w-full flex items-start gap-1 group rounded-md px-1 py-1 transition-colors",
        isCurrent ? "bg-accent/50" : "hover:bg-accent/30",
        isDragging && "shadow-lg ring-2 ring-primary z-10",
      )}
    >
      {/* Drag handle + slide number */}
      <div className="flex flex-col items-center mt-1.5 min-w-[14px]">
        <span
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-0.5 -mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <GripVertical className="w-3 h-3 text-muted-foreground" />
        </span>
        <span
          className={cn(
            "text-[10px] font-medium tabular-nums",
            isCurrent ? "text-primary" : "text-muted-foreground",
          )}
        >
          {index + 1}
        </span>
      </div>

      {/* Thumbnail */}
      <button
        onClick={onClick}
        className={cn(
          "flex-1 aspect-video rounded overflow-hidden transition-all relative cursor-pointer",
          isCurrent
            ? "ring-2 ring-primary shadow-sm"
            : "ring-1 ring-border group-hover:ring-muted-foreground/50",
        )}
      >
        {hasThumbnail ? (
          <img
            src={url!}
            alt={`Slide ${index + 1}: ${slide.title}`}
            className="w-full h-full object-cover"
            draggable={false}
          />
        ) : (
          // Soft placeholder with a skewed sheen sweeping left-to-right via
          // GPU-accelerated translateX (smoother than animating
          // background-position). Animation runs whenever there's no
          // thumbnail — covers loading (in-flight fetch) AND error (e.g.
          // 404 because capture hasn't completed yet) so the user always
          // sees motion until a JPEG lands. Once capture completes the
          // by-slide-id endpoint succeeds, `hasThumbnail` flips true, and
          // the <img> replaces this placeholder.
          //
          // Contrast bumped to foreground/0.08 → 0.22 so the sheen is
          // clearly visible against the muted gradient on both light and
          // dark themes (the previous 0.04 → 0.10 was nearly invisible on
          // light bg).
          <div className="relative w-full h-full overflow-hidden bg-gradient-to-br from-muted/40 via-muted/60 to-muted/40">
            <div className="absolute inset-y-0 -inset-x-1/4 animate-shimmer-sweep bg-[linear-gradient(90deg,transparent_0%,hsl(var(--foreground)/0.08)_30%,hsl(var(--foreground)/0.22)_50%,hsl(var(--foreground)/0.08)_70%,transparent_100%)] will-change-transform" />
          </div>
        )}

        {/* Per-slide settings gear (hover) — existing surface for
            transition/interval/duration overrides. Unchanged. */}
        <SlideSettings
          slide={slide}
          previewIframeRef={previewIframeRef}
          devContainerId={devContainerId}
          getIdToken={getIdToken}
        >
          <button
            onClick={(e) => e.stopPropagation()}
            className="absolute top-0.5 right-0.5 p-0.5 rounded bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/60"
            aria-label={`Slide ${index + 1} settings`}
          >
            <Settings2 className="w-2.5 h-2.5 text-white" />
          </button>
        </SlideSettings>

        {/* Hover-kebab actions menu — covers touch / a11y / users who
            don't think to right-click. `data-no-dnd` + stopPropagation on
            pointerdown prevent dnd-kit's 5px activation from swallowing
            the click as a drag. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-no-dnd
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              className="absolute top-0.5 right-5 p-0.5 rounded bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/60"
              aria-label={`Slide ${index + 1} actions`}
            >
              <MoreVertical className="w-2.5 h-2.5 text-white" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side="right"
            className="w-44 p-1"
            onClick={(e) => e.stopPropagation()}
          >
            {renderMenuItems(DropdownMenuItem, DropdownMenuSeparator)}
          </DropdownMenuContent>
        </DropdownMenu>
      </button>
    </div>
  );

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>{rowContent}</ContextMenuTrigger>
        <ContextMenuContent className="w-44 p-1">
          {renderMenuItems(ContextMenuItem, ContextMenuSeparator)}
        </ContextMenuContent>
      </ContextMenu>

      {/* AlertDialog is hoisted OUTSIDE both menus so Radix doesn't
          unmount it when the parent menu content closes. See plan §10. */}
      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this slide?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{slide.title}&quot; will be removed permanently. This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmDeleteOpen(false);
                onDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});
